import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { TemplateState } from '../../../types/common/types';
import { GridViewWebComponent } from '../../../grid/gridView';
import { defaultPageSizeOptions } from '../../../grid/gridView/types';
import {
  AccountAction,
  Column,
  ColumnKeys,
  MobileSecurityCompanyGridGridItem,
  MobileSecurityCompanyGridWebComponentSchema,
  PREDEFINED_FILTERS_KEYS,
} from '../types';
import { MobileSecurityCompanyDataProvider } from '../dataProvider/MobileSecurityCompanyDataProvider';
import { WEB_COMPONENT_GRID_VIEW, WEB_COMPONENT_MOBILE_SECURITY_CUSTOMER_VIEW } from '../../../webcomponents.constants';
import { mobileSecurityAccountActionsRenderer } from './customGridRenderers';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { MobileSecurityCustomerViewWebComponent } from '../../mobileSecurityCustomerView';
import { showCreateAccountDialog } from '../../mobileSecurityCreateAccountDialog';
import { showErrorToast, showSuccessToast } from '@nirvana/shared/common';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzProgressSpinnerWebComponent } from '../../../ui/gzProgressSpinner';

export class MobileSecurityCompanyGridView extends BaseWebComponentView {
  dom: {
    $wrapper: HTMLElement;
    $title: GzTitleWebComponent;

    $gridWrapper: HTMLElement;
    $gridView: GridViewWebComponent<Column, ColumnKeys, MobileSecurityCompanyGridGridItem>;

    $customerWrapper: HTMLElement;
    $customerView: MobileSecurityCustomerViewWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $title: this.shadow.querySelector('#page-title'),

      $gridWrapper: this.shadow.querySelector('.grid-wrapper'),
      $gridView: this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW),

      $customerWrapper: this.shadow.querySelector('.customer-wrapper'),
      $customerView: this.shadow.querySelector(WEB_COMPONENT_MOBILE_SECURITY_CUSTOMER_VIEW),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = this.stringsTranslator.getMobileSecurityStrings();
  }

  setTranslatedStrings() {
    this.dom.$title.innerText = this.translations.title;
  }

  async buildSuccessView(data: MobileSecurityCompanyGridWebComponentSchema) {
    this.buildGridView(data);
  }

  public toggleActionButtonsLoadingState(companyId: string, shouldBeLoading: boolean) {
    const [buttonOpenConsole, buttonSendEmails] = this.getActionButtonsForCompany(companyId);
    const spinner = this.getActionsSpinnerForCompany(companyId);

    if (buttonOpenConsole) {
      buttonOpenConsole.style.display = shouldBeLoading ? 'none' : 'inline-block';
    }

    if (buttonSendEmails) {
      buttonSendEmails.style.display = shouldBeLoading ? 'none' : 'inline-block';
    }

    if (spinner) {
      spinner.style.display = shouldBeLoading ? 'inline-block' : 'none';
    }
  }

  private buildGridView(data: MobileSecurityCompanyGridWebComponentSchema) {
    if (this.dependencies.canHaveChildCompanies()) {
      this.showPartenerGrid(data);
    } else {
      this.showCustomerView();
    }
  }

  private showPartenerGrid(data: MobileSecurityCompanyGridWebComponentSchema) {
    toggleVisibility(this.dom.$customerWrapper, false);
    toggleVisibility(this.dom.$gridWrapper, true);

    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start().then().catch(console.error);
    }

    this.dom.$gridView.sideMenuParent = this.shadow;

    this.dom.$gridView.propertiesSet = false;

    this.dom.$gridView.viewId = data.view.id;
    this.dom.$gridView.dataProvider = new MobileSecurityCompanyDataProvider(this.dependencies);
    this.dom.$gridView.allColumnDetails = data.columns;
    this.dom.$gridView.columns = data.view.displayedColumns;
    this.dom.$gridView.filters = data.view.filters;
    this.dom.$gridView.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
    this.dom.$gridView.isCompactViewChecked = false;
    this.dom.$gridView.defaultViewProps = data.view;
    this.dom.$gridView.activeViewProps = data.view;
    this.dom.$gridView.activeViewInitialProps = data.view;
    this.dom.$gridView.pageSize = data.view.pageSize;
    this.dom.$gridView.hasPagination = true;
    this.dom.$gridView.pageSizeOptions = defaultPageSizeOptions;
    this.dom.$gridView.enableCSVExport = false;
    this.dom.$gridView.hasSelectableRows = false;

    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.MOBILE_SECURITY_ACTIONS]: mobileSecurityAccountActionsRenderer(this.translations, this.dom.$gridView),
    };

    this.dom.$gridView.propertiesSet = true;
  }

  private showCustomerView() {
    toggleVisibility(this.dom.$customerWrapper, true);
    toggleVisibility(this.dom.$gridWrapper, false);

    if (this.dom.$customerView.injectDependencies) {
      this.dom.$customerView.injectDependencies(this.dependencies);
      this.dom.$customerView.start().then().catch(console.error);
    }

    if (this.dom.$customerView.started) {
      this.dom.$customerView.controller.render();
    }
  }

  public showCreateAccountDialog(details: { name: string; fromCustomerView: boolean }, callback: () => Promise<void>) {
    showCreateAccountDialog(details.name, !details.fromCustomerView, this.translations, callback);
  }

  public showCreateAccountSuccessToast() {
    showSuccessToast(this.translations.createAccountSuccessToast);
  }

  public showCreateAccountErrorToast(error?: Error) {
    showErrorToast(error ?? new Error(this.translations.createAccountErrorToast));
  }

  buildErrorView(error: Error) {
    console.error(error);
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private getActionButtonsForCompany(companyId: string): [GzButtonWebComponent | null, GzButtonWebComponent | null] {
    const buttonOpenConsoleId = `#${AccountAction.CONSOLE}-${companyId}`;
    const buttonOpenConsole = this.dom.$gridView.shadowRoot?.querySelector(buttonOpenConsoleId) as GzButtonWebComponent;
    const buttonSendEmailsId = `#${AccountAction.ACTIVATION}-${companyId}`;
    const buttonSendEmails = this.dom.$gridView.shadowRoot?.querySelector(buttonSendEmailsId) as GzButtonWebComponent;

    return [buttonOpenConsole ?? null, buttonSendEmails ?? null];
  }

  private getActionsSpinnerForCompany(companyId: string): GzProgressSpinnerWebComponent | null {
    const spinnerId = `#spinner-${companyId}`;
    const spinner = this.dom.$gridView.shadowRoot?.querySelector(spinnerId) as GzProgressSpinnerWebComponent;

    return spinner ?? null;
  }
}
